import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Form } from '@qodo/design-system';
import { useQueryClient } from '@tanstack/react-query';
import { LoaderCircle } from 'lucide-react';
import { SYNC_STATUSES } from 'network/httpClients/RepositoryClient/consts';
import { useSyncGitInstallationMutation } from 'network/mutations/useSyncGitInstallationMutation';
import { getErrorToastContent } from 'network/mutations/utils';
import { QUERY_KEYS } from 'network/queries/consts';
import { useGerritImportStatusQuery } from 'network/queries/useGerritImportStatusQuery';
import { InstallationStatusMessage, Step } from 'shared/InstallationDialog';
import { useMultiStepDialog } from 'shared/MultiStepDialog';
import { spawn } from 'utils/translations';
import { FORM_IDS, IMPORT_PROGRESS_TRANSLATION_KEY } from '../consts';
import { useGerritInstallation } from '../gerritInstallationContext';
import { WebhookSetup } from '../parts/WebhookSetup';

const spawnImport = spawn(IMPORT_PROGRESS_TRANSLATION_KEY);

/**
 * View 2 — auto-import progress. Creating the installation already kicked off
 * the server-side import, so this step only polls import-status: a spinner
 * while importing, a done state on completion, or an error with a Retry that
 * re-runs the installation sync. On completion it surfaces the Qodo webhook URL
 * for the user to register on their Gerrit server, falling back to the
 * "webhooks assumed configured" note until the backend can resolve the URL.
 */
export const ImportProgress = () => {
  const { setIsNextEnabled, goNext } = useMultiStepDialog();
  const {
    installationId,
    webhookUrl,
    gerritHost,
    username,
    apiPrefix,
    setIsFinalized,
  } = useGerritInstallation();

  const methods = useForm();
  const queryClient = useQueryClient();

  const { data: importStatus } = useGerritImportStatusQuery(installationId);
  const {
    mutate: retryImport,
    isPending: isRetrying,
    error: retryError,
  } = useSyncGitInstallationMutation();

  const savedCount = importStatus?.savedCount ?? 0;
  const isCompleted = importStatus?.status === SYNC_STATUSES.SUCCESS;
  const importFailed = importStatus?.status === SYNC_STATUSES.FAILED;
  const isImporting = !isCompleted && !importFailed;

  useEffect(() => {
    if (isCompleted) {
      setIsFinalized(true);
    }
  }, [isCompleted, setIsFinalized]);

  useEffect(() => {
    setIsNextEnabled(isCompleted);
  }, [isCompleted, setIsNextEnabled]);

  // Step 2 is the wizard's last step: the footer "Done" submit completes it.
  const onSubmit = () => {
    if (isCompleted) {
      goNext();
    }
  };

  // Retry re-runs the standard installation reconcile, then resumes polling.
  const onRetry = () => {
    if (!installationId) {
      return;
    }

    retryImport(
      { installationId },
      {
        onSuccess: () =>
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.GERRIT_IMPORT_STATUS, installationId],
          }),
      },
    );
  };

  const failureDetail =
    importStatus?.error ??
    getErrorToastContent(retryError, spawnImport('errors.import-failed'));

  const title = isCompleted
    ? spawnImport('results-title')
    : spawnImport('title');

  return (
    <Step title={title}>
      <Form
        {...({ id: FORM_IDS.IMPORT_PROGRESS } as Record<string, string>)}
        methods={methods}
        onSubmit={onSubmit}
      >
        {isImporting && (
          <div className="flex flex-col gap-2.5">
            <div className="flex items-center gap-2.5 p-4 rounded-lg border border-border-bg-neutral">
              <LoaderCircle className="size-4 shrink-0 animate-spin text-muted-foreground" />
              <span className="text-sm">{spawnImport('importing')}</span>
            </div>
            {webhookUrl && (
              <p className="text-xs text-text-general-neutral-mid">
                {spawnImport('webhook-ready-hint')}
              </p>
            )}
          </div>
        )}

        {isCompleted && (
          <>
            <InstallationStatusMessage
              status="success"
              message={spawnImport('done-title', { count: savedCount })}
              detail={
                webhookUrl ? undefined : spawnImport('assumed-webhooks-note')
              }
            />
            {webhookUrl && (
              <WebhookSetup
                webhookUrl={webhookUrl}
                gerritHost={gerritHost}
                username={username}
                apiPrefix={apiPrefix}
              />
            )}
          </>
        )}

        {importFailed && (
          <InstallationStatusMessage
            status="error"
            message={spawnImport('errors.import-failed')}
            detail={failureDetail}
            retryLabel={spawnImport('retry-label')}
            onRetry={onRetry}
            isRetrying={isRetrying}
          />
        )}
      </Form>
    </Step>
  );
};
